import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { store, useAppSelector } from '@/app/store';
import { RoleContext } from '@/app/api/common';
import { checkNoError, ChannelInfo, copyChannelInfo, copyUrlInfo, copyUrls,getChannelByEpgId, getNewChannelInfo, getUrl, getField, setField, UrlInfo } from '@/features/channels/channelsSlice';
import { LanguageInfo } from '@/features/languages/languagesSlice';

import BreadCrumbs from '@/components/Common/BreadCrumbs';
import Button from '@/components/Common/Button';
import Dropdown, { FieldInfo } from '@/components/Common/Dropdown';
import FlexRow, { FlexRowProps } from '@/components/Common/FlexRow';
import Input from '@/components/Common/Input';
import MenuItem from '@/components/Common/MenuItem';
import Tabs from '@/components/Common/Tabs';
import UrlsEditor from '@/components/Common/UrlsEditor';

import { usePageSubscription } from '@/app/websocket/hooks';
import { CatHeader } from '@/pages/Projects/Project';
import Projects from '@/pages/Projects/Projects';

import { ChannelListsProps } from '@/pages/ChannelLists/ChannelLists';
import { getChannelPageUrl } from './Channels';

export interface NumberTextProps {
  children?:			React.ReactNode;
}

const NumberText = (props: NumberTextProps) => {
  return (
  <span className="text-[#1B69AA]">
    { props.children }
  </span>
  );
};

const Channel = (props: ChannelListsProps) => {
  const { t } = useTranslation();

  const { epg_id, tab } = useParams();
  const location = useLocation();

  const navigate = useNavigate();
  const role = React.useContext(RoleContext);

  // Subscribe to channel change notifications
  usePageSubscription(
    props.project
      ? [{ type: 'channel', scope_id: props.project.id }]
      : [{ type: 'channel' }]
  );

  // Track which channel is currently being viewed for WS notification filtering
  React.useEffect(() => {
    if (epg_id) store.setCurrentChannel(epg_id);
    return () => { store.setCurrentChannel(null); };
  }, [epg_id]);

  const globalChannels = useAppSelector((state) => state.channels);
  const hotelChannels = useAppSelector((state) => state.hotelChannels);
  
  const languages = useAppSelector((state) => state.languages);
  
  const [channel, setChannel] = React.useState(null as ChannelInfo | null);
  const [changes, setChanges] = React.useState(false);
  const [triedSave, SetTriedSave] = React.useState(false);
  const [urlInvalid, setUrlInvalid] = React.useState(false);
  const [epgIdDupe, setEpgIdDupe] = React.useState(false);
  
  const trackFormats = useAppSelector((state) => state.trackFormats);

  React.useEffect(() => {
    if (role.admin || role?.privilages?.includes("ViewTrackFormats")) {
      store.getTrackFormats();
    }
  }, [role]);

  const overridesTracksOpts = React.useMemo(() => {
    return [
      { value: "", desc: "Default" },
      ...(trackFormats?.track_formats?.map((tf: any) => ({
        value: tf.key,
        desc: tf.description,
      })) || []),
    ];
  }, [trackFormats.track_formats]);

  const [selectedCodec, setSelectedCodec] = React.useState<string>("");

  const selectedField = React.useMemo<FieldInfo>(() => {
    const opt = overridesTracksOpts.find((o: FieldInfo) => o.value === selectedCodec);
    return {
      value:	selectedCodec,
      desc:		opt ? opt.desc : "Default",
    };
  }, [overridesTracksOpts, selectedCodec]);
  
  const handleSave = React.useRef(() => { return; });
  
  const epgId = epg_id;
  const channels =	props.project
					?	hotelChannels
					:	globalChannels; 
  				
  const caption =	epgId === "__new__"
  					?	t('channels:channel:new')
  					:	(	channel
  							?	`${channel.name} (${channel.epg_id})`
  							:	t('global:loading')
  						);
  
  console.log("Channel/channel=", channel);
  
  React.useEffect(() => {
    if (	props.project &&
       		(	!hotelChannels.channels ||
       			hotelChannels.channels.length == 0
       		) &&
       		(role.admin || role?.privilages?.includes("ViewChannels") || role?.privilages?.includes("ChangeChannels"))
       )
      store.getChannels(props.project.id);
  }, []);
  
  React.useEffect(() => {
    if (!props.registerCallback)
      return;
    props.registerCallback("save_channel", () => handleSave.current());
  }, [props.registerCallback]);
  
  React.useEffect(() => {
    console.log("Channel/epgId=" + epgId + "/channels=", channels, "/channel=", channel);
    if (!epgId)
      return;
    
    if (	epgId === '__new__' &&
    		channel &&
    		channel.epg_id == ''
       )
      return;
    
    let chan =	epgId === '__new__'
    			?	getNewChannelInfo()
    			:	getChannelByEpgId(epgId, channels)
    
    if (!chan)
      return;
    
    if (tab === "clone") {
      chan = copyChannelInfo(chan);
      
      chan.is_new = true;
      chan.epg_id = "";
      chan.old_epg_id = "";
      
	  SetTriedSave(true);
    }
    
    setChannel(chan);
	setChanges(false);
  }, [epgId, channels.channels, tab]);

  // Fire API when navigating to searchproject screen
  React.useEffect(() => {
    const isSearchProjectPage = tab === "searchproject" || location.pathname.endsWith('/searchproject');
    if (isSearchProjectPage && channel?.epg_id) {
      // Clear previous data before fetching new data
      store.clearChannelListProjects();
      store.getProjectByEpgID(channel.epg_id);
    }
  }, [tab, location.pathname, channel?.epg_id]);

  const epgIdInvalid = React.useMemo(() => {
    return !channel || channel.epg_id.trim() =='';
  }, [channel]);

  const epgIdHasInvalidChars = React.useMemo(() => {
    return !!channel && /[/\\?]/.test(channel.epg_id);
  }, [channel]);
  
  const nameInvalid = React.useMemo(() => {
    return !channel || channel.name.trim() =='';
  }, [channel]);
  
  const languagesOpts = React.useMemo(() => {
    return languages.languages.map(
      (lang: LanguageInfo) => (
        {
          value:	lang.code,
          desc:		lang.name,
        }
      )
    );
  }, [languages.languages]);
  
  const languagesInvalid = React.useMemo(() => {
    return	!channel ||
    		!channel.languages ||
    		!channel.languages.length;
  }, [channel]);
  
  const updateEpgId = React.useCallback((epgId: string) => {
    if (!channel)
      return;

    let chan = copyChannelInfo(channel);
    chan.epg_id = epgId;
    
    setChannel(chan);
    setEpgIdDupe(false);
    
    updateChanges(true);
  }, [channel]);
  
  const updateChanges = React.useCallback((val: boolean) => {
    setChanges(true);
    if (	val &&
    		props.setChanges
       )
      props.setChanges(val);
  }, [props.setChanges]);
  
  const updateName = React.useCallback((val: string) => {
    if (!channel)
      return;
    
    let chan = copyChannelInfo(channel);
    chan.name = val;
    
    setChannel(chan);
    updateChanges(true);
  }, [channel]);
  
  const updateRealEpgId = React.useCallback((val: string) => {
    if (!channel)
      return;
    
    let chan = copyChannelInfo(channel);
    chan.real_epg_id = val;
    
    setChannel(chan);
    updateChanges(true);
  }, [channel]);
  
  const updateLanguages = React.useCallback((val: string[]) => {
    if (!channel)
      return;
    
    let chan = copyChannelInfo(channel);
    chan.languages = val.filter((lang: string) => lang != '');
    
    setChannel(chan);
    updateChanges(true);
  }, [channel]);
  
  const updateLogo = React.useCallback((val: string) => {
    if (!channel)
      return;
    
    let chan = copyChannelInfo(channel);
    chan.logo = val;
    
    setChannel(chan);
    updateChanges(true);
  }, [channel]);
  
  const updateUrls = React.useCallback((urls: UrlInfo[]) => {
    if (!channel)
      return;
    
    let chan = copyChannelInfo(channel);
    chan.urls = urls;
    
    setChannel(chan);
    updateChanges(true);
  }, [channel]);
  
  /*const updateUrl = React.useCallback((i: number, urlinf: UrlInfo) => {
    if (!channel)
      return;
    
    let chan = copyChannelInfo(channel);
    chan.urls[i] = urlinf;
    
    let invalid = false;
    const types: string[] = [];
    
    for (let i=0; i < chan.urls.length; i++) {
      let url = chan.urls[i];
      
      url.is_invalid = types.indexOf(url.type) > -1;
      chan.urls[i] = url;
      
      if (!url.is_invalid) {
        types.push(url.type);
      } else {
        invalid = true;
      }
    }
    
    setUrlInvalid(invalid);
    
    console.log("Channel/updateUrl/i=" + i + "/invalid=" + invalid + "/urlinf=", urlinf, "/chan=", chan);
    setChannel(chan);
    updateChanges(true);
  }, [channel]);
  
  const deleteUrl = React.useCallback((i: number) => {
    if (!channel)
      return;
    
    let chan = copyChannelInfo(channel);
    chan.urls.splice(i, 1);
    
    setChannel(chan);
    updateChanges(true);
  }, [channel]);
  
  const addUrl = React.useCallback(() => {
    if (!channel)
      return;
    
    let chan = copyChannelInfo(channel);
    chan.urls.push({
      type:			"",
      url:			"",
      tags:			[],
    });
    
    console.log("Channel/addUrl/chan=", chan);
    setChannel(chan);
    updateChanges(true);
  }, [channel]);*/
  const handleSearchProjectById = () => {
    if (!channel || !channel.epg_id) {
      return;
    }
    navigate(getChannelPageUrl(channel.epg_id, props.project, "searchproject"));
  }

  handleSave.current = async () => {
    //console.log("Channel/handleSave/channel=", channel);
    
    if (!channel)
      return;
    
    if (nameInvalid || epgIdInvalid || epgIdHasInvalidChars || epgIdDupe || languagesInvalid|| urlInvalid) {
      SetTriedSave(true);
      return;
    }
    
    const clone = tab === "clone";
    
    if (	clone &&
    		!channel.is_new
       ) {
      console.log("Channel/handleSave/Unexpected operation: Trying to clone a channel, but it wasn't marked new");
      return;
    }
    
    let wasNew = channel && channel.is_new;
    let chan = copyChannelInfo(channel);
    let i = 0;
    
    if (channel.urls) {
      while (i < channel.urls.length) {
        if (!channel.urls[i].type) {
          channel.urls.splice(i, 1);
          continue;
        }
        
        i++;
      }
    }
    
    const mess = await store.setChannel(channel, props.project ? props.project.id : '');
    console.log("Channel/handleSave/mess=", mess);
    
    if (!checkNoError(mess)) {
      if (	mess &&
      		mess.errors &&
      		mess.errors.length &&
      		mess.errors[0].error.indexOf("already exists")
      	 )
        setEpgIdDupe(true);
      return;
    }
    
    SetTriedSave(false);

    store.getChannels(props.project ? props.project.id : '');

    //if (clone) {
    //  navigate(getChannelPageUrl("", props.project));
    //} else
    if (	wasNew &&
       		mess &&
       		mess.errors &&
      		mess.errors.length &&
      		mess.errors[0].result
       )
      navigate(getChannelPageUrl(mess.errors[0].result.epg_id, props.project));
  };
  
  const crumbs = [];
  if (!props.project)
    crumbs.push([
    	`/channels`,
     	t('channels:channels'),
    ]);

  const isSearchProject = tab === "searchproject" || location.pathname.endsWith('/searchproject');
  const channelUrl = isSearchProject ? getChannelPageUrl(channel?.epg_id || '', props.project) : '';
  crumbs.push([
    channelUrl,
   	caption,
  ]);

  if (isSearchProject) {
    crumbs.push([
      '',
      t('channel_lists:options:search_projects'),
    ]);
  }

  const isNew = channel && channel.is_new;
  const isClone = tab === "clone";
  
  const cUrls =	channel &&
  				channel.urls
  				?	channel.urls
  				:	[];
  
  return (
  <React.Fragment>
  {!isClone &&
	<FlexRow direction="column"
	         align="start"
	         className={[
               'px-[32px] bg-white',
               props.project ? '' : 'pt-[32px] sticky top-[64px] z-50',
             ]}>
	  <BreadCrumbs pages={crumbs} />
	</FlexRow>
  }
   {!isSearchProject && <FlexRow direction="column"
             align="start"
             className="px-[32px]">
      <FlexRow direction="column"
		       align="start"
		       className="py-0 gap-[16px] max-w-[448px]">
      <React.Fragment>
		<CatHeader caption={	isClone
								?	'channels:channel:clone:caption_n'
								:	(	isNew
										?	'channels:channel:add_new'
										:	'channels:channel:edit'
									)
						   }
				   captionProps={{n: isClone && channel ? channel.name : ''}} />
		<Input label={t(
				isClone
					?	'channels:channel:clone:caption'
					:	'channels:channel:epg_id:caption'
			   ) + '*'}
		       placeholder={ isClone ? t('channels:channel:clone:placeholder') : ''}
		       isInvalid={(triedSave && (epgIdInvalid || epgIdDupe)) || epgIdHasInvalidChars}
		       isDisabled={!isNew}
		       errorMessage={epgIdDupe ? t('channels:channel:epg_id:duplicate') : epgIdHasInvalidChars ? t('channels:channel:epg_id:invalid_chars') : t('channels:channel:epg_id:invalid')}
		       value={channel ? channel.epg_id : ''}
		       onValueChange={updateEpgId} />
	  {//(!isClone || nameInvalid) &&
		<Input label={t('channels:channel:name:caption') + '*'}
		       placeholder={t('channels:channel:name:placeholder')}
		       isInvalid={triedSave && nameInvalid}
		       errorMessage={epgIdDupe ? t('channels:channel:epg_id:duplicate') : t('channels:channel:epg_id:invalid')}
		       value={channel ? channel.name : ''}
		       onValueChange={updateName} />
	  }
	  {!isClone &&
		<React.Fragment>
		  <Input label={t('channels:channel:real_epg_id:caption')}
		         value={channel ? channel.real_epg_id : ''}
		         onValueChange={updateRealEpgId} />
		  <Input label={t('channels:channel:logo:caption')}
		         placeholder={t('channels:channel:logo:placeholder')}
		         value={channel ? channel.logo : ''}
		         onValueChange={updateLogo} />
    </React.Fragment>
	  }
	  {//(!isClone || languagesInvalid) &&
        <Dropdown label={t('channels:channel:languages:caption') + '*'}
              value={channel ? channel.languages : []}
              selectionMode="multiple"
              options={languagesOpts}
              isInvalid={triedSave && languagesInvalid}
              errorMessage={t('channels:channel:languages:invalid')}
              setValue={updateLanguages}>
		  <>
		  </>
		</Dropdown>
	  }
	  {!isClone &&
	    <React.Fragment>
	      <CatHeader caption="channels:channel:tracks"
	                 margin="mt-[8px]" />
	      <Dropdown label={t('channels:channel:track_format')}
	                value={selectedCodec}
	                options={overridesTracksOpts}
	                setValue={(val: any) => setSelectedCodec(typeof val === 'string' ? val : '')}>
	        <>
	        </>
	      </Dropdown>
	      <UrlField field={selectedField}
	                kind="default"
	                urls={cUrls}
	                setUrls={updateUrls} />
	      <UrlField field={selectedField}
	                kind="tracks"
	                urls={cUrls}
	                setUrls={updateUrls} />
	      <UrlField field={selectedField}
	                kind="track-json"
	                urls={cUrls}
	                setUrls={updateUrls} />
	      <CatHeader caption="channels:grid:urls"
	                 margin="mt-[8px]" />
	      <UrlsEditor urls={cUrls}
				      setUrls={updateUrls}
				      triedSave={triedSave}
				      setUrlInvalid={setUrlInvalid} />
	    </React.Fragment>
	  }
	  </React.Fragment>
	  {/*
	  {channel && channel.urls.map((url: UrlInfo, i: number) =>
	    <UrlRow key={i}
	            url={url}
	            setUrl={(newUrl: UrlInfo) => updateUrl(i, newUrl)}
	            triedSave={triedSave}
	            requestDelete={() => deleteUrl(i)} />
	  )}
	    <FlexRow flex={0}
	             className="gap-[5px] cursor-pointer text-[--Primetime-main]"
	             onClick={() => addUrl()}>
	      <span className="material-icons !text-[--Primetime-main] text-[16px]">
	        add
	      </span>
	      { t('channels:channel:urls:add') }
	    </FlexRow>
	  */}
	  </FlexRow>
    </FlexRow>
  }
  {!props.project && !isSearchProject && (role.admin || role?.privilages?.includes("ChangeChannels") || role?.privilages?.includes("ViewChannels")) &&
    <FlexRow className={
               isClone
               ?	"absolute bottom-0 p-[32px] bg-white z-10 justify-end gap-[16px]"
               :	"sticky bottom-0 p-[32px] bg-white z-10 gap-[16px]"
             }>
    {isClone &&
      <Button className="uppercase"
              caption={t('channels:channel:clone:cancel')}
              onClick={() => channel &&
                             navigate(getChannelPageUrl("", props.project))}
              layout="white" />
    }
    {(role.admin || role?.privilages?.includes("ChangeChannels")) &&
      <Button caption={t('channels:channel:save')}
              icon="save"
              isDisabled={!changes || (triedSave && (nameInvalid || epgIdInvalid || epgIdDupe || languagesInvalid || urlInvalid))}
              onClick={() => handleSave.current()} />
    }
      <Button className='space-between'
              caption={t('channel_lists:options:search_projects')}
              icon="search"
              onClick={() => handleSearchProjectById()}
              layout="white"/>
    </FlexRow>
  }
  {isSearchProject &&
    <FlexRow direction="column"
             align="start"
             className="px-[32px] pt-[16px]">
      <Projects hideContainer={true} />
    </FlexRow>
  }
  </React.Fragment>
  );
};

type UrlFieldKind = 'default' | 'tracks' | 'track-json';

interface UrlFieldProps {
  field:			FieldInfo;
  kind:				UrlFieldKind;
  urls:				UrlInfo[];
  setUrls:			(urls: UrlInfo[]) => any;
}

const URL_FIELD_BASE: Record<UrlFieldKind, string> = {
  'default':		'default',
  'tracks':			'default-tracks',
  'track-json':		'tracks',
};

const URL_FIELD_LABEL_KEY: Record<UrlFieldKind, string> = {
  'default':		'channels:channel:urls:-n',
  'tracks':			'channels:channel:urls:tracks_n',
  'track-json':		'channels:channel:urls:track_json',
};

const UrlField = (props: UrlFieldProps) => {
  const { t } = useTranslation();

  const base = URL_FIELD_BASE[props.kind];

  const fname =	base +
				(	props.field.value
					?	"-" + props.field.value
					:	""
				);
  const val = getField(props.urls, fname);

  return (
  <Input label={t(URL_FIELD_LABEL_KEY[props.kind], {desc: props.field.desc})}
         value={val}
         onValueChange={(nval: string) => {
           let urls = props.urls.map((url: UrlInfo) => copyUrlInfo(url));
           urls = setField(urls, fname, nval);

           props.setUrls(urls);
         }} />
  );
};

export default Channel;


